import { Injectable, OnDestroy } from '@angular/core';
import { z } from 'zod';
import {
  SavedItemCreate,
  SavedItemResponse,
  SavedItemsService,
  SavedItemUpdate,
} from 'ldt-state-storage-api';
import { AuthService } from '../../auth/service/auth.service';
import { NotificationService } from '../../shared/notification-service/notification.service';
import { Log } from '../../logger.service';
import { QuickBuild } from '../../people/quick-build.service';
import {
  BaseStoredItem,
  StoredItemConfig,
} from '../../shared/components/stored-items-browser/stored-items-browser.component';
import { UIFilterGroupSchema } from '../../people/quick-build-validators';
import { StateStorageBaseService } from './state-storage-base.service';
import { QuickBuildItemRendererComponent } from '../../shared/components/stored-items-browser/quick-build-item-renderer.component';

// Zod schema for validation
const StoredQuickBuildPayloadSchema = z.object({
  category: z.string().nullable().optional(),
  rootFilterGroup: UIFilterGroupSchema, // Using the proper validator from quick-build-validators
  reports: z.any().optional(),
});

// Extended QuickBuild interface with storage metadata
export interface StoredQuickBuild extends QuickBuild {
  category?: string;
  scope?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

@Injectable({
  providedIn: 'root',
})
export class QuickBuildStorageService
  extends StateStorageBaseService<StoredQuickBuild>
  implements OnDestroy
{
  constructor(
    stateStorageService: SavedItemsService,
    authService: AuthService,
    notificationService: NotificationService
  ) {
    super(stateStorageService, authService, notificationService);
  }

  getResourceType(): string {
    return 'quick_build';
  }

  toApiModel(quickBuild: StoredQuickBuild): SavedItemCreate | SavedItemUpdate {
    return {
      name: quickBuild.name,
      description: quickBuild.description,
      payload: {
        category: quickBuild.category,
        rootFilterGroup: quickBuild.rootFilterGroup,
        reports: quickBuild.reports,
      },
      scope: (quickBuild.scope || 'org') as 'org' | 'public',
      item_type_name: this.getResourceType(),
    };
  }

  fromApiModel(response: SavedItemResponse): StoredQuickBuild | null {
    const rawPayload =
      typeof response.payload === 'string' ? JSON.parse(response.payload) : response.payload;

    try {
      // Validate the payload structure using zod
      const payload = StoredQuickBuildPayloadSchema.parse(rawPayload);

      const storedQuickBuild: StoredQuickBuild = {
        id: response.id.toString(),
        name: response.name || 'Untitled Quick Build',
        description: response.description || '',
        category: payload.category || undefined,
        rootFilterGroup: payload.rootFilterGroup,
        reports: payload.reports,
        scope: response.scope,
        createdAt: response.created_at,
        updatedAt: response.updated_at,
        createdBy: response.created_by?.name || '',
      };

      return storedQuickBuild;
    } catch (error) {
      // Enhanced error logging for validation failures
      if (error instanceof z.ZodError) {
        Log.e('QuickBuildStorageService error', error);
        Log.s('QuickBuild validation failed', 'error', {
          type: this.getResourceType(),
          id: response.id,
          name: response.name,
          validationErrors: JSON.stringify(error.errors),
          payload: rawPayload,
        });
      } else {
        Log.s('Failed to parse QuickBuild from API', 'error', {
          type: this.getResourceType(),
          id: response.id,
          name: response.name,
          payload: rawPayload,
          error: String(error),
        });
      }
      return null;
    }
  }

  // Helper method to get QuickBuilds by category
  getByCategory(category: string): StoredQuickBuild[] {
    const items = this.getCachedItems();
    if (!items) return [];
    return items.filter((qb) => qb.category === category);
  }

  // Public method to access cached items
  getCachedItems(): StoredQuickBuild[] | null {
    return super.getCachedItems();
  }

  // Override create to add success notification
  protected afterCreate(
    item: StoredQuickBuild,
    response: SavedItemResponse
  ): StoredQuickBuild | null {
    const created = this.fromApiModel(response);
    if (created) {
      this.notificationService.success('New quick build saved');
    }
    return created;
  }

  // Override delete to add success notification
  protected afterDelete(id: string): void {
    this.notificationService.success('Quick build deleted');
  }

  public static copyStoredQuickBuild(qb: StoredQuickBuild): StoredQuickBuild {
    return JSON.parse(JSON.stringify(qb));
  }

  getBrowserConfig(): StoredItemConfig<StoredQuickBuild & BaseStoredItem> {
    return {
      title: 'Quick Builds',
      itemTypeName: 'Quick Builds',
      description: 'Select from pre-configured filter sets to quickly analyze your data',
      searchPlaceholder: 'Search quick builds...',
      useCategories: true,
      itemCountLabel: (count) => `${count} quick build${count !== 1 ? 's' : ''}`,
      allowDelete: true,
      onDelete: (item) => this.delete(item.id).subscribe(),
      defaultComponentRenderer: QuickBuildItemRendererComponent,
    };
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
